import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, Utensils, Bell } from "lucide-react";
import { plannerAPI, formatTime, addDays, ymd, recipePhotoUrl } from "@/lib/planner";

/**
 * PlannerShared — public read-only view at /planner/shared/{token}.
 * No auth, no writes. Meant for family / co-parents / babysitters.
 * If the token doesn't exist (revoked, rotated), we show a friendly "no
 * longer active" state.
 */
export default function PlannerShared() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const d = await plannerAPI.publicShare(token);
        setData(d);
      } catch (e) {
        setErr(e?.response?.status === 404 ? "This share link is no longer active." : "Couldn't load this Planner.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  if (loading) {
    return <div className="min-h-screen bg-[#F7F3ED] flex items-center justify-center text-sm text-[#3E4A37]">Loading…</div>;
  }
  if (err || !data) {
    return (
      <div className="min-h-screen bg-[#F7F3ED] flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <div className="text-2xl italic text-[#3E4A37]" style={{ fontFamily: "Caveat, cursive" }}>Willoah</div>
          <div className="text-[10px] tracking-[0.35em] text-[#A87F56] mt-1">PLANNER</div>
          <div className="mt-6 text-sm text-[#2A2823]/70">{err}</div>
          <div className="mt-4">
            <Link to="/" className="text-sm text-[#3E4A37] underline underline-offset-2">Visit Willoah</Link>
          </div>
        </div>
      </div>
    );
  }

  const owner = data.owner;
  const weekStart = new Date(data.week.start + "T00:00:00");
  const byDay = {};
  for (const e of data.week.events) {
    try { const k = ymd(new Date(e.start_at)); (byDay[k] = byDay[k] || []).push(e); } catch {}
  }

  return (
    <div className="min-h-screen bg-[#F7F3ED] text-[#2A2823]" data-testid="planner-shared" style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}>
      <div className="max-w-4xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="text-center pb-6 border-b border-[#A87F56]/25 mb-8">
          <div className="text-3xl italic text-[#3E4A37]" style={{ fontFamily: "Caveat, cursive" }}>Willoah</div>
          <div className="text-[10px] tracking-[0.35em] text-[#A87F56] mt-1">PLANNER</div>
          <div className="mt-4 text-sm text-[#2A2823]/70">
            {owner.username || owner.name}'s family view · read-only
          </div>
        </div>

        {/* Today */}
        <section className="bg-white rounded-2xl border border-[#A87F56]/25 p-6 mb-6" data-testid="planner-shared-today">
          <div className="text-[10px] tracking-[0.35em] text-[#A87F56] mb-3">TODAY</div>
          {data.today.events.length === 0 ? (
            <div className="text-sm italic text-[#2A2823]/55 py-4 text-center">Nothing on today.</div>
          ) : (
            <ul className="space-y-2">
              {data.today.events.map((e) => (
                <li key={e.id} className="flex items-center gap-3 text-[15px] text-[#2A2823]">
                  <div className="w-9 h-9 rounded-full bg-[#EDE5DA] flex items-center justify-center shrink-0">
                    {e.event_type === "reminder" ? <Bell className="w-4 h-4 text-[#3E4A37]" /> : <Calendar className="w-4 h-4 text-[#3E4A37]" />}
                  </div>
                  <div className="w-20 text-xs text-[#2A2823]/60 tabular-nums shrink-0">{e.all_day ? "All day" : formatTime(e.start_at)}</div>
                  <div className={`flex-1 min-w-0 truncate ${e.done_at ? "line-through text-[#2A2823]/40" : ""}`}>{e.title}</div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Week */}
        <section className="bg-white rounded-2xl border border-[#A87F56]/25 p-6 mb-6" data-testid="planner-shared-week">
          <div className="text-[10px] tracking-[0.35em] text-[#A87F56] mb-3">THIS WEEK</div>
          <div className="space-y-3">
            {[...Array(7)].map((_, i) => {
              const d = addDays(weekStart, i);
              const key = ymd(d);
              const events = byDay[key] || [];
              if (events.length === 0) return null;
              return (
                <div key={key} className="flex gap-3 text-sm">
                  <div className="w-16 text-[#2A2823]/60 shrink-0">{d.toLocaleDateString(undefined, { weekday: "short", day: "numeric" })}</div>
                  <ul className="flex-1 space-y-1">
                    {events.map((e) => (
                      <li key={e.id} className="text-[#2A2823]">
                        <span className="text-[#2A2823]/60 mr-2">{e.all_day ? "" : formatTime(e.start_at)}</span>{e.title}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </section>

        {/* Meals */}
        <section className="bg-white rounded-2xl border border-[#A87F56]/25 p-6" data-testid="planner-shared-meals">
          <div className="text-[10px] tracking-[0.35em] text-[#A87F56] mb-4">MEAL PLAN</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {[...Array(7)].map((_, i) => {
              const d = addDays(weekStart, i);
              const key = ymd(d);
              const entry = data.meals?.find((m) => m.date === key);
              return (
                <div key={key} className="flex items-center gap-3 py-1.5">
                  <div className="w-6 text-[10px] tracking-widest text-[#2A2823]/45 uppercase">{d.toLocaleDateString(undefined, { weekday: "narrow" })}</div>
                  {entry?.recipe?.photo_path ? (
                    <img src={recipePhotoUrl(entry.recipe.photo_path)} alt={entry.recipe.title} className="w-9 h-9 rounded-lg object-cover bg-[#EDE5DA]" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  ) : (
                    <div className="w-9 h-9 rounded-lg bg-[#EDE5DA] flex items-center justify-center"><Utensils className="w-4 h-4 text-[#3E4A37]/60" /></div>
                  )}
                  <div className="flex-1 text-sm text-[#2A2823] truncate">{entry?.recipe?.title || <span className="italic text-[#2A2823]/45">—</span>}</div>
                </div>
              );
            })}
          </div>
        </section>

        <div className="mt-8 text-center text-xs text-[#2A2823]/55">
          Made with <Link to="/planner" className="text-[#3E4A37] underline underline-offset-2">Willoah Planner</Link>
        </div>
      </div>
    </div>
  );
}
